/**
 * Package Advance Collection – Pure utility helpers
 *
 * Shared helper functions extracted to eliminate duplication (DRY) across
 * usePackageAdvanceCollection, InvoiceTable, and related components.
 *
 * All functions are pure (no side-effects, no hooks) and fully unit-testable.
 */
import { formatDateDMY, getTodayYMD } from '../../../../utils/dateUtils';
import {
  ADVANCE_PAYMENT_MODE_KEY,
  PKG_ADVANCE_METHOD_LABEL,
  PKG_ADVANCE_NOTES,
} from './config';
import type {
  AdvanceConsumptionMetadata,
  InvoiceRowData,
  PaymentDetails,
  PaymentHistoryItem,
} from './types';

// ============================================================================
// Balance / amount parsing
// ============================================================================

/**
 * Parse the `balanceAmt` string from an invoice row into a numeric value.
 *
 * Strips everything except digits and decimal points, returns 0 for
 * non-numeric or empty strings.
 *
 * @example parseBalanceAmount({ balanceAmt: '₹294.68' }) → 294.68
 */
export const parseBalanceAmount = (row: Pick<InvoiceRowData, 'balanceAmt'>): number => {
  const raw = row.balanceAmt?.replace(/[^0-9.]/g, '') || '0';
  const parsed = parseFloat(raw);
  return isNaN(parsed) ? 0 : parsed;
};

// ============================================================================
// Payment details builder
// ============================================================================

/**
 * Build a `PaymentDetails` object from an invoice row.
 *
 * Centralises the "row → payment details" mapping so it is defined once and
 * reused everywhere (hook + InvoiceTable + settle-after-advance callback).
 */
export const buildPaymentDetails = (
  row: Pick<InvoiceRowData, 'patientName' | 'patientId' | 'patient_id' | 'mrn' | 'balanceAmt'>,
  currency: string,
): PaymentDetails => ({
  date: formatDateDMY(),
  patientName: row.patientName || 'Patient',
  patientId: String(row.patientId ?? row.patient_id ?? ''),
  patientNumericId: row.patientId ?? row.patient_id,
  mrnNo: row.mrn || undefined,
  // Fallback to 'INR' so the API payload never receives an empty currency string
  currency: currency || 'INR',
  netAmount: parseBalanceAmount(row as InvoiceRowData),
});

// ============================================================================
// Consume-from-package computation
// ============================================================================

/**
 * Compute the total "consume from package" amount from advance metadata.
 *
 * For each metadata item the remaining amount is `requested - consumed`.
 * The sum is what should be auto-deducted (shown in the banner).
 */
export const computeConsumeAmount = (
  metadata: AdvanceConsumptionMetadata[] | null | undefined,
): number => {
  if (!Array.isArray(metadata) || metadata.length === 0) return 0;
  return metadata.reduce((sum, item) => {
    const requested = parseFloat(item.requested_amount || '0');
    const consumed = parseFloat(item.consumed_amount || '0');
    return sum + Math.max(requested - consumed, 0);
  }, 0);
};

// ============================================================================
// Per-package remaining-amount map
// ============================================================================

/**
 * Group advance metadata by `package_id` and sum the remaining required
 * amount (`requested - consumed`) per package.
 *
 * Used both for building the validate API params and for constructing
 * package-advance payment items.
 */
export const buildPackageAmountMap = (
  metadata: AdvanceConsumptionMetadata[],
): Map<number, number> => {
  const map = new Map<number, number>();
  metadata.forEach((item) => {
    const requested = parseFloat(item.requested_amount || '0');
    const consumed = parseFloat(item.consumed_amount || '0');
    const remaining = Math.max(requested - consumed, 0);
    map.set(item.package_id, (map.get(item.package_id) || 0) + remaining);
  });
  return map;
};

// ============================================================================
// Package-advance payment items builder
// ============================================================================

/** Minimal shape needed to look up the advance payment mode */
export interface PaymentModeEntry {
  id: number;
  name: string;
  code: string;
}

/**
 * Find the "Advance" payment mode from the clinic's configured modes.
 *
 * Matches case-insensitively against `name` or `code`.
 */
export const findAdvancePaymentMode = (
  modes: PaymentModeEntry[],
): PaymentModeEntry | undefined =>
  modes.find(
    (m) =>
      m.name.toLowerCase() === ADVANCE_PAYMENT_MODE_KEY ||
      m.code.toLowerCase() === ADVANCE_PAYMENT_MODE_KEY,
  );

/**
 * Build `PaymentHistoryItem[]` for each package's remaining amount.
 *
 * These items represent the auto-deducted package-advance portions that get
 * merged into `payment_details` before the API call.
 *
 * @returns Empty array when no advance mode is found or metadata is empty.
 */
export const buildPackagePaymentItems = (
  metadata: AdvanceConsumptionMetadata[] | null | undefined,
  paymentModes: PaymentModeEntry[],
): PaymentHistoryItem[] => {
  if (!Array.isArray(metadata) || metadata.length === 0) return [];

  const advanceMode = findAdvancePaymentMode(paymentModes);
  if (!advanceMode) {
    // Warn so a missing / misconfigured payment mode is immediately visible
    // in dev-tools instead of silently dropping package payment items.
    // eslint-disable-next-line no-console
    console.warn(
      '[PackageAdvanceCollection] No advance payment mode found in paymentModes.',
      'Check that a mode whose name/code matches "advance" is configured for this clinic.',
      { paymentModes },
    );
    return [];
  }

  const pkgMap = buildPackageAmountMap(metadata);
  const items: PaymentHistoryItem[] = [];

  pkgMap.forEach((amount, packageId) => {
    if (amount > 0) {
      items.push({
        id: `pkg-${packageId}-${Date.now()}`,
        method: PKG_ADVANCE_METHOD_LABEL,
        amount,
        payment_mode_id: advanceMode.id,
        package_id: packageId,
        date: getTodayYMD(),
        notes: PKG_ADVANCE_NOTES,
      });
    }
  });

  return items;
};

// ============================================================================
// Package name extraction from validate API response
// ============================================================================

/** Minimal credit-transaction shape needed for name extraction */
interface CreditTransactionLike {
  package_name?: string;
}

/** Minimal package-validation-result shape needed for name extraction */
interface PackageResultLike {
  credit_transactions?: CreditTransactionLike[];
  balance_amount?: number;
}

/**
 * Extract deduplicated package name(s) from the validate API response.
 *
 * Joins multiple unique names with ", ".  Returns `undefined` when no names
 * are found (so the caller can use `??` default).
 */
export const extractPackageNames = (
  packages: PackageResultLike[] | undefined | null,
): string | undefined => {
  if (!packages || packages.length === 0) return undefined;

  const names = packages
    .flatMap((pkg) => pkg.credit_transactions ?? [])
    .map((ct) => ct.package_name)
    .filter(Boolean) as string[];

  const unique = names.filter((name, idx, arr) => arr.indexOf(name) === idx);
  return unique.length > 0 ? unique.join(', ') : undefined;
};

/**
 * Sum the `balance_amount` across all packages in the validate API response.
 */
export const sumPackageBalance = (
  packages: PackageResultLike[] | undefined | null,
): number => {
  if (!packages || packages.length === 0) return 0;
  return packages.reduce((sum, pkg) => sum + (pkg.balance_amount ?? 0), 0);
};
